import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ColumnConfig, SortDirections, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortingMetadata, TextAlignment } from "@app/shared/waffles/exports";
import { AdminUsersContext, AdminUsersProvider } from "@app/admin/contexts/AdminUsersContext";
import { UserModelFromApi, UserStatus } from "@app/admin/user-administration/userModel";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { UserContext } from "@app/user/UserContext";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import CheckboxDropdown from "@app/shared/checkbox-dropdown/CheckboxDropdown";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import Roles from "@app/user/roles";

interface UsersListTableData {
  clientId: number;
  clientName: JSX.Element; // quest admin view only
  email: string;
  formalName: JSX.Element;
  phoneNumber: string;
  status: JSX.Element;
  userName: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const UsersListContent: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [loading, setLoading] = useState(true);
  // todo - this component's state should be converted to a reducer pattern with fewer objects,
  // so as to reduce the number of duplicate API calls (e.g. useEffect being hit twice when clicking "Search"
  // on a page after the first)
  // that would also make the SortingMetadata interface unnecessary
  const [skip, setSkip] = useState(0);
  const [take] = useState(50);
  const [sortDetails, setSortDetails] = useState<SortingMetadata<UsersListTableData>>({
    sortDirection: SortDirections.None,
    sortedColumn: null,
  });
  const urlParams = new URLSearchParams(window.location.search);
  const [searchUsername, setSearchUsername] = useState(() => urlParams.get("uName") ?? "");
  const [searchEmail, setSearchEmail] = useState(() => urlParams.get("email") ?? "");
  const [searchName, setSearchName] = useState(() => urlParams.get("name") ?? "");
  const [searchClient, setSearchClient] = useState(() => urlParams.get("client") ?? "");
  const [selectedUserStatusItems, setSelectedUserStatusItems] = useState<DropdownItem<UserStatus>[]>(() => getSelectedUserStatusDropdownItems(urlParams.get("status")));
  const [isFiltered, setIsFiltered] = useState(false);
  const adminUsersContext = useContext(AdminUsersContext);
  const user = useContext(UserContext);
  const userIsQuestAdmin = useMemo(() => { return user.roles.includes(Roles.QUEST_ADMIN) }, []);
  const userIsClientAdmin = useMemo(() => { return user.roles.includes(Roles.CLIENT_ADMIN) }, []);

  useEffect(() => {
    setLoading(true);
    fetchUsers(false, adminUsersContext.users.length === 0, false);
  }, [skip, sortDetails]);

  const fetchUsers = (skipShouldBeZero?: boolean, updateCount?: boolean, searchCleared?: boolean) => {
    const shouldSkip = skipShouldBeZero ? 0 : skip;
    const name = searchCleared ? "" : searchName;
    const username = searchCleared ? "" : searchUsername;
    const email = searchCleared ? "" : searchEmail;
    const client = searchCleared ? "" : searchClient;
    const userStatuses: UserStatus[] = searchCleared ? null : selectedUserStatusItems.map(i => i.key);

    if (userIsQuestAdmin) {
      adminUsersContext.getAllUsers(shouldSkip, take, name, username, email, client, userStatuses, sortDetails.sortDirection, sortDetails.sortedColumn);
      if (updateCount) {
        adminUsersContext.getUserCount(name, username, email, client, userStatuses);
      }
    } else if (userIsClientAdmin) {
      adminUsersContext.getUsersByClient(user.clientId, shouldSkip, take, name, username, email, client, userStatuses, sortDetails.sortDirection, sortDetails.sortedColumn);
      if (updateCount) {
        adminUsersContext.getUserCountByClient(user.clientId, name, username, email, client, userStatuses);
      }
    }

    if (searchCleared) {
      setIsFiltered(false);
      selectAllUserStatusItems();
    } else {
      setIsFiltered(!!(searchName || searchUsername || searchEmail || searchClient || getAllUserStatusDropdownItems().length !== userStatuses.length));
    }

    const searchParams = new URLSearchParams(window.location.search);

    if (!!name) {
      searchParams.set("name", name);
    } else {
      searchParams.delete("name");
    }
    if (!!username) {
      searchParams.set("uName", username);
    } else {
      searchParams.delete("uName");
    }
    if (!!email) {
      searchParams.set("email", email);
    } else {
      searchParams.delete("email");
    }
    if (!!client) {
      searchParams.set("client", client);
    } else {
      searchParams.delete("client");
    }
    if (!!userStatuses) {
      searchParams.set("status", JSON.stringify(userStatuses));
    } else {
      searchParams.delete("status");
    }

    history.replaceState(null, "", window.location.pathname + "?" + searchParams.toString());
  }

  const selectAllUserStatusItems = () => {
    setSelectedUserStatusItems(getAllUserStatusDropdownItems());
  }
  const widthLimitingStyle: React.CSSProperties = {
    display: "inline-block",
    maxWidth: "250px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }

  const getUserStatus = (user: UserModelFromApi) => {
    switch (user.status) {
      case UserStatus.ACTIVATED:
        return (<TooltipWrapper text="User is Activated"><QesIcon iconName={QesIcons.Unlock} description="Unlocked" className="text-success"/></TooltipWrapper>);
      case UserStatus.UNCONFIRMED:
        return (<TooltipWrapper text="Email is not confirmed"><QesIcon iconName={QesIcons.MailV1} description="Email" className="text-warning" /></TooltipWrapper>);
      case UserStatus.DEACTIVATED:
        return (<TooltipWrapper text="User is Deactivated"><QesIcon iconName={QesIcons.LockV1} description="Locked" className="text-danger" /></TooltipWrapper>);
      case UserStatus.DELETED:
        return (<TooltipWrapper text="User is Deleted"><QesIcon iconName={QesIcons.XCircle} description="Deleted" className="text-danger" /></TooltipWrapper>);
      default:
        throw "Invalid user.status : " + user.status + " for userId " + user.userId;
    }
  }

  const usersListTableData: UsersListTableData[] = useMemo<UsersListTableData[]>(() => {
    /* oxlint-disable sort-keys */
    const data = adminUsersContext.users.map((user) => ({
      status: getUserStatus(user),
      userName: <Link style={widthLimitingStyle} key={user.userName} to={`/admin/user-administration/user/${user.userId}`}>{user.userName}</Link>,
      email: user.email,
      formalName: <span style={widthLimitingStyle} key={user.formalName}>{user.formalName}</span>,
      phoneNumber: user.phoneNumber,
      clientName: <Link key={user.clientId} to={`/admin/client-administration/${user.clientId}`}>{user.clientName}</Link>, // quest admin view only
      clientId: user.clientId,
      [wafflesKeyColumnName]: user.userId.toString(),
    }));
    /* oxlint-enable sort-keys */
    setLoading(false);
    return data;
  }, [adminUsersContext.users]);

  const columnConfig = useMemo<ColumnConfig>(() => {
    // only formalName, email, userName, clientName and status are implemented for sorting in the API
    return {
      clientId: {
        displayText: "",
        hidden: true,
        testId: "client-id",
      },
      clientName: {
        displayText: "Client",
        hidden: !user.roles.includes(Roles.QUEST_ADMIN),
        testId: "client",
      },
      email: {
        displayText: "Email",
        testId: "email",
      },
      formalName: {
        displayText: "Name",
        testId: "formal-name",
      },
      phoneNumber: {
        displayText: "Phone",
        noSort: true,
        testId: "phone",
      },
      status: {
        alignment: TextAlignment.Center,
        displayText: "Status",
        testId: "status",
      },
      userName: {
        displayText: "User Name",
        testId: "user-name",
      },
    }
  }, []);

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setSkip((pageNumber - 1) * take),
    recordsPerPage: take,
    skip: skip,
    totalRecords: adminUsersContext.userCount,
  }

  const handleSort = (data: UsersListTableData[], newSortedColumn: keyof UsersListTableData, direction: SortDirections) => {
    setSortDetails({
      sortDirection: direction,
      sortedColumn: newSortedColumn,
    });
    setSkip(0);
    return data;
  }

  const handleSearch = () => {
    setSkip(0);
    fetchUsers(true, true);
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const clearFilter = () => {
    setSearchClient("");
    setSearchEmail("");
    setSearchName("");
    setSearchUsername("");
    setSelectedUserStatusItems(getAllUserStatusDropdownItems());
    setIsFiltered(false);

    setSkip(0);
    fetchUsers(true, true, true);
  }

  return (
    <Page documentTitle="Users - Admin">
      <Breadcrumbs template={BreadcrumbTemplateEnum.UserAdministration} items={[]} />
      <PageInner>
        <PageHeader title={"Users"} iconName={QesIcons.UserV1} disableHR={false}>
          <PageHeaderRight>
            <Link
              className="qes-button qes-button-primary"
              to="/admin/user-administration/create"
            >
              Add New User
            </Link>
          </PageHeaderRight>
        </PageHeader>
        <PageSection>
          <div className="users-admin-filters mb-3 mt-5">
            <QESTextInput containerClassName="users-admin-filters__input" label="Username" testId="users-filter__username" value={searchUsername} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setSearchUsername(e.target.value)} />
            <QESTextInput containerClassName="users-admin-filters__input" label="Email" testId="users-filter__email" value={searchEmail} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setSearchEmail(e.target.value)} />
            <QESTextInput containerClassName="users-admin-filters__input" label="Name" testId="users-filter__name" value={searchName} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setSearchName(e.target.value)} />
            {(user.roles.includes(Roles.QUEST_ADMIN)) && <QESTextInput containerClassName="users-admin-filters__input" label="Client" testId="users-filter__client" value={searchClient} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setSearchClient(e.target.value)} />}
            <CheckboxDropdown
              buttonClassNames="me-2"
              buttonText="Status"
              cancelText="Cancel"
              disableEmptySelection={true}
              loading={loading}
              items={getAllUserStatusDropdownItems()}
              selectedItems={selectedUserStatusItems}
              hideButtons={true}
              onSelectionChanged={(selectedItems: Array<DropdownItem<UserStatus>>) => { if (selectedItems.length === 0) { selectAllUserStatusItems(); } else { setSelectedUserStatusItems(selectedItems); } }}
            />
            <QESButton qesType="primary" qesTestId="users-admin__search-button" onClick={handleSearch}>Search</QESButton>
            <QESButton qesType="secondary" qesTestId="users-admin__clear-filters" onClick={clearFilter} disabled={!isFiltered}>Clear</QESButton>
          </div>
          <p>Displaying {usersListTableData.length} of {adminUsersContext.userCount} users {isFiltered ? "(filtered)" : ""}</p>
          <Waffles
            columns={columnConfig}
            customSort={handleSort}
            data={usersListTableData}
            enablePagination={true}
            loading={loading}
            paginationProps={paginationProps}
            passThroughSorting={true}
            testId="users-list"
          />
        </PageSection>
      </PageInner>
    </Page>
  );
}

const getSelectedUserStatusDropdownItems = (selectedKeysJson: string) => {
  if (!selectedKeysJson)
    return getAllUserStatusDropdownItems();

  const selectedKeys = JSON.parse(selectedKeysJson);

  if (Array.isArray(selectedKeys) && selectedKeys.length > 0)
    return getAllUserStatusDropdownItems().filter(item => selectedKeys.includes(item.key));
  else
    return getAllUserStatusDropdownItems();
}

const getAllUserStatusDropdownItems = () => {
  const items: Array<DropdownItem<UserStatus>> = [];
  items.push({ key: UserStatus.ACTIVATED, name: "Activated" });
  items.push({ key: UserStatus.UNCONFIRMED, name: "Unconfirmed" });
  items.push({ key: UserStatus.DEACTIVATED, name: "Deactivated" });
  items.push({ key: UserStatus.DELETED, name: "Deleted" });

  return items;
}

const WrappedUsersList: React.FC<React.PropsWithChildren<unknown>> = () => (
  <AdminUsersProvider>
    <UsersListContent />
  </AdminUsersProvider>
)

export { WrappedUsersList as UsersList };